import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { TrendingUp, RefreshCw, CheckCircle, ArrowLeft, Lock, Eye, EyeOff } from "lucide-react";
import toast from "react-hot-toast";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";

function parseError(data) {
  // FastAPI can return detail as string or array of validation errors
  if (!data) return "Something went wrong";
  if (typeof data.detail === "string") return data.detail;
  if (Array.isArray(data.detail)) return data.detail[0]?.msg || "Validation error";
  return "Something went wrong";
}

function PasswordStrength({ password }) {
  const checks = [
    password.length >= 8,
    /[A-Z]/.test(password),
    /[0-9]/.test(password),
    /[^A-Za-z0-9]/.test(password),
  ];
  const score = checks.filter(Boolean).length;
  const colors = ["#ff4757", "#ff8c42", "#ffd166", "#00d4a1"];
  const labels = ["Weak", "Fair", "Good", "Strong"];
  if (!password) return null;
  return (
    <div style={{ marginTop: "6px" }}>
      <div style={{ display: "flex", gap: "4px", marginBottom: "4px" }}>
        {checks.map((pass, i) => (
          <div key={i} style={{ flex: 1, height: "4px", borderRadius: "2px", background: i < score ? colors[score - 1] : "rgba(232,234,240,0.1)", transition: "background 0.2s" }} />
        ))}
      </div>
      <div style={{ fontSize: "11px", color: score > 0 ? colors[score - 1] : "var(--text-muted)" }}>
        {labels[score - 1] || "Too short"}
      </div>
    </div>
  );
}

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState("email"); // email → otp → newpass → done
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // ── Step 1: Send OTP ──────────────────────────────────────────
  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Enter your email"); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/forgot-password?email=${encodeURIComponent(email)}`, { method: "POST" });
      const data = await res.json();
      if (data.otp_for_dev) {
        toast.success(`DEV OTP: ${data.otp_for_dev}`, { duration: 15000 });
      } else {
        toast.success("Reset code sent! Check your email.");
      }
      setStep("otp");
    } catch {
      toast.error("Could not send reset code. Try again.");
    }
    setLoading(false);
  };

  // ── Step 2: OTP input ─────────────────────────────────────────
  const handleOTPChange = (i, val) => {
    if (!/^[0-9]?$/.test(val)) return;
    const next = [...otp];
    next[i] = val;
    setOtp(next);
    if (val && i < 5) document.getElementById(`rotp-${i + 1}`)?.focus();
  };

  const handleOTPKey = (i, e) => {
    if (e.key === "Backspace" && !otp[i] && i > 0) document.getElementById(`rotp-${i - 1}`)?.focus();
  };

  const handleVerifyOTP = () => {
    const code = otp.join("");
    if (code.length !== 6) { toast.error("Enter all 6 digits"); return; }
    // OTP is verified server-side when the password is actually reset
    toast.success("Code accepted! Set your new password.");
    setStep("newpass");
  };

  // ── Step 3: Reset password ─────────────────────────────────────
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword || !confirmPassword) { toast.error("Fill in both fields"); return; }
    if (newPassword !== confirmPassword) { toast.error("Passwords do not match"); return; }
    if (newPassword.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (!/[A-Z]/.test(newPassword)) { toast.error("Add at least one uppercase letter"); return; }
    if (!/[0-9]/.test(newPassword)) { toast.error("Add at least one number"); return; }
    if (!/[^A-Za-z0-9]/.test(newPassword)) { toast.error("Add at least one special character"); return; }

    setLoading(true);
    try {
      // Send as JSON body — NOT query params (security fix)
      const res = await fetch(`${API}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          otp_code: otp.join(""),
          new_password: newPassword,
          confirm_password: confirmPassword,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 429) {
          toast.error("Too many attempts. Please wait a few minutes.");
        } else {
          toast.error(parseError(data));
        }
        setLoading(false);
        return;
      }
      toast.success("Password reset successfully!");
      setStep("done");
    } catch {
      toast.error("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  const Logo = () => (
    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "28px" }}>
      <div style={{ width: "40px", height: "40px", background: "var(--accent-green)", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <TrendingUp size={20} color="#0a0f1e" />
      </div>
      <div>
        <div style={{ fontFamily: "Syne, sans-serif", fontSize: "20px", fontWeight: "800", color: "var(--text-primary)" }}>ProTrackNG</div>
        <div style={{ color: "var(--text-secondary)", fontSize: "12px" }}>Tender Intelligence Platform</div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg-primary)", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px", fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .fp-card { width: 100%; max-width: 420px; background: var(--bg-card); border: 1px solid var(--border); border-radius: 20px; padding: 40px; box-shadow: 0 40px 80px rgba(0,0,0,0.4); }
        .fp-input { width: 100%; padding: 11px 14px; background: rgba(232,234,240,0.04); border: 1px solid rgba(232,234,240,0.1); border-radius: 10px; color: var(--text-primary); font-size: 14px; font-family: inherit; outline: none; transition: border-color 0.2s; box-sizing: border-box; }
        .fp-input:focus { border-color: rgba(0,212,161,0.5); }
        .fp-input::placeholder { color: rgba(232,234,240,0.3); }
        .fp-btn { width: 100%; padding: 12px; border-radius: 10px; border: none; background: linear-gradient(135deg, #00d4a1, #00b888); color: #0a0f1e; font-size: 14px; font-weight: 700; cursor: pointer; font-family: inherit; display: flex; align-items: center; justify-content: center; gap: 8px; transition: opacity 0.2s; }
        .fp-btn:hover { opacity: 0.88; }
        .fp-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .fp-back { display: flex; align-items: center; gap: 6px; background: none; border: none; color: var(--text-muted); cursor: pointer; font-size: 13px; font-family: inherit; margin-top: 16px; padding: 0; }
        @media (max-width: 480px) {
          .fp-card { padding: 28px 20px; border-radius: 16px; }
        }
      `}</style>

      <div style={{ width: "100%", maxWidth: "420px" }}>

        {/* ── STEP 1: EMAIL ── */}
        {step === "email" && (
          <div className="fp-card">
            <Logo />
            <h2 style={{ fontSize: "22px", fontWeight: "800", marginBottom: "8px", color: "var(--text-primary)" }}>Forgot Password?</h2>
            <p style={{ color: "var(--text-secondary)", fontSize: "13px", marginBottom: "28px", lineHeight: "1.6" }}>
              Enter your registered email and we'll send a 6-digit reset code.
            </p>
            <form onSubmit={handleSendOTP} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <div>
                <label style={{ fontSize: "13px", color: "var(--text-secondary)", marginBottom: "6px", display: "block" }}>Email Address</label>
                <input className="fp-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" required autoFocus />
              </div>
              <button type="submit" disabled={loading} className="fp-btn">
                {loading ? <><RefreshCw size={14} style={{ animation: "spin 1s linear infinite" }} /> Sending...</> : "Send Reset Code →"}
              </button>
            </form>
            <button onClick={() => navigate("/login")} className="fp-back">
              <ArrowLeft size={14} /> Back to Login
            </button>
          </div>
        )}

        {/* ── STEP 2: OTP ── */}
        {step === "otp" && (
          <div className="fp-card">
            <Logo />
            <h2 style={{ fontSize: "22px", fontWeight: "800", marginBottom: "8px", color: "var(--text-primary)" }}>Enter Reset Code</h2>
            <p style={{ color: "var(--text-secondary)", fontSize: "13px", marginBottom: "24px", lineHeight: "1.6" }}>
              We sent a 6-digit code to <strong style={{ color: "var(--accent-green)" }}>{email}</strong>. Check your inbox (and spam folder).
            </p>
            <div style={{ display: "flex", gap: "8px", justifyContent: "center", marginBottom: "28px" }}>
              {otp.map((d, i) => (
                <input
                  key={i} id={`rotp-${i}`} value={d}
                  onChange={e => handleOTPChange(i, e.target.value)}
                  onKeyDown={e => handleOTPKey(i, e)}
                  maxLength={1} autoFocus={i === 0}
                  style={{ width: "46px", height: "54px", textAlign: "center", fontSize: "22px", fontWeight: "800", fontFamily: "monospace", background: "rgba(232,234,240,0.04)", border: d ? "2px solid var(--accent-green)" : "1px solid rgba(232,234,240,0.1)", borderRadius: "10px", color: "var(--text-primary)", outline: "none" }}
                />
              ))}
            </div>
            <button onClick={handleVerifyOTP} disabled={loading} className="fp-btn" style={{ marginBottom: "12px" }}>
              <CheckCircle size={14} /> Verify Code
            </button>
            <button onClick={() => { setOtp(["","","","","",""]); setStep("email"); }} className="fp-back">
              <ArrowLeft size={14} /> Back
            </button>
          </div>
        )}

        {/* ── STEP 3: NEW PASSWORD ── */}
        {step === "newpass" && (
          <div className="fp-card">
            <Logo />
            <h2 style={{ fontSize: "22px", fontWeight: "800", marginBottom: "8px", color: "var(--text-primary)" }}>Set New Password</h2>
            <p style={{ color: "var(--text-secondary)", fontSize: "13px", marginBottom: "24px" }}>Choose a strong password for your account.</p>
            <form onSubmit={handleResetPassword} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <div>
                <label style={{ fontSize: "13px", color: "var(--text-secondary)", marginBottom: "6px", display: "block" }}>New Password</label>
                <div style={{ position: "relative" }}>
                  <input
                    className="fp-input"
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    placeholder="Min 8 chars, uppercase, number, symbol"
                    required autoFocus
                    style={{ paddingRight: "44px" }}
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "rgba(232,234,240,0.4)", cursor: "pointer", padding: "4px" }}>
                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                <PasswordStrength password={newPassword} />
              </div>
              <div>
                <label style={{ fontSize: "13px", color: "var(--text-secondary)", marginBottom: "6px", display: "block" }}>Confirm Password</label>
                <input
                  className="fp-input"
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Repeat password"
                  required
                  style={{ borderColor: confirmPassword && confirmPassword !== newPassword ? "#ff4757" : undefined }}
                />
                {confirmPassword && confirmPassword !== newPassword && (
                  <div style={{ fontSize: "11px", color: "#ff4757", marginTop: "4px" }}>Passwords do not match</div>
                )}
              </div>
              <button type="submit" disabled={loading} className="fp-btn" style={{ marginTop: "4px" }}>
                {loading ? <><RefreshCw size={14} style={{ animation: "spin 1s linear infinite" }} /> Resetting...</> : <><Lock size={14} /> Reset Password</>}
              </button>
            </form>
          </div>
        )}

        {/* ── STEP 4: DONE ── */}
        {step === "done" && (
          <div className="fp-card" style={{ textAlign: "center" }}>
            <Logo />
            <div style={{ width: "64px", height: "64px", background: "rgba(0,212,161,0.1)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
              <CheckCircle size={32} color="var(--accent-green)" />
            </div>
            <h2 style={{ fontSize: "22px", fontWeight: "800", marginBottom: "8px", color: "var(--text-primary)" }}>Password Reset!</h2>
            <p style={{ color: "var(--text-secondary)", fontSize: "13px", marginBottom: "28px", lineHeight: "1.6" }}>
              Your password has been updated successfully. You can now log in with your new password.
            </p>
            <button onClick={() => navigate("/login")} className="fp-btn">
              Go to Login →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
